// Phase K.3 (2026-08-06) — Customer statements list page.
//
// Route: /customer/statements (customer role only)
//
// Design mirrors CustomerInvoicesListPage.jsx: DashboardShell +
// a card per statement showing period, total, status badge, and
// action buttons ("View" / "Download PDF"). Pay-now is offered
// inline for sent statements.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { DashboardShell } from "@/components/DashboardShell";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
    formatStatementIdentifier, formatStatementPeriod,
} from "@/lib/statementDisplay";
import {
    FileText, ChevronRight, CheckCircle2, AlertTriangle, Clock, Loader2,
    Download,
} from "lucide-react";

const STATUS_LABEL = {
    sent: "Sent",
    processing: "Processing",
    paid: "Paid",
    failed: "Payment failed",
};

const STATUS_COLOR = {
    sent: "bg-blue-50 text-blue-900 ring-blue-200",
    processing: "bg-amber-50 text-amber-900 ring-amber-200",
    paid: "bg-emerald-100 text-emerald-900 ring-emerald-200",
    failed: "bg-red-50 text-red-700 ring-red-200",
};

const STATUS_ICON = {
    sent: FileText,
    processing: Clock,
    paid: CheckCircle2,
    failed: AlertTriangle,
};

function fmtMoney(cents) {
    if (cents == null) return "—";
    const sign = cents < 0 ? "-" : "";
    const v = Math.abs(cents) / 100;
    return `${sign}$${v.toLocaleString(undefined, {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
    })}`;
}

function fmtDate(s) { if (!s) return "—"; return s.slice(0, 10); }

export default function CustomerStatementsListPage() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get("/customer/statements");
                if (!cancelled) setItems(data.items || []);
            } catch (err) {
                if (!cancelled) toast.error(formatApiError(err));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const totalOutstanding = items
        .filter((s) => s.status === "sent" || s.status === "failed")
        .reduce((n, s) => n + (s.total_cents || 0), 0);

    return (
        <DashboardShell>
            <div className="mx-auto max-w-5xl px-4 py-8"
                 data-testid="customer-statements-list-page">
                <div className="mb-6">
                    <h1 className="text-2xl font-semibold text-slate-900">
                        Statements
                    </h1>
                    <p className="text-sm text-slate-500 mt-1">
                        Consolidated monthly statements bundling multiple invoices into a single ACH charge.
                    </p>
                </div>

                {totalOutstanding > 0 && (
                    <Card className="mb-6 bg-blue-50/40 border-blue-200"
                          data-testid="customer-statements-outstanding-banner">
                        <CardContent className="p-4 flex items-center justify-between">
                            <div>
                                <div className="text-xs uppercase tracking-wider text-slate-500">
                                    Total outstanding
                                </div>
                                <div className="text-2xl font-semibold text-blue-900 mt-0.5">
                                    {fmtMoney(totalOutstanding)}
                                </div>
                            </div>
                            <div className="text-sm text-slate-600">
                                Across {items.filter((s) => s.status === "sent" || s.status === "failed").length} statement(s)
                            </div>
                        </CardContent>
                    </Card>
                )}

                {loading && (
                    <div className="py-12 text-center text-slate-500">
                        <Loader2 className="h-6 w-6 animate-spin inline mr-2" />
                        Loading…
                    </div>
                )}

                {!loading && items.length === 0 && (
                    <Card data-testid="customer-statements-empty">
                        <CardContent className="p-8 text-center text-slate-500">
                            <FileText className="h-8 w-8 mx-auto mb-3 text-slate-300" />
                            No statements yet.
                            <div className="mt-1 text-xs">
                                Statements are generated automatically on your billing day, or manually by your account manager.
                            </div>
                        </CardContent>
                    </Card>
                )}

                {!loading && items.length > 0 && (
                    <div className="space-y-3">
                        {items.map((s) => {
                            const Icon = STATUS_ICON[s.status] || FileText;
                            return (
                                <Card
                                    key={s.id}
                                    className="hover:shadow-sm transition-shadow"
                                    data-testid={`customer-statement-card-${s.id}`}
                                >
                                    <CardContent className="p-4 flex items-center gap-4">
                                        <div className="hidden sm:flex h-11 w-11 rounded bg-slate-50 items-center justify-center border border-slate-100">
                                            <Icon className="h-5 w-5 text-slate-500" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-0.5">
                                                <span className="font-medium text-slate-900 truncate"
                                                      title={s.id}>
                                                    {formatStatementIdentifier(s)}
                                                </span>
                                                <span
                                                    className={`text-xs px-2 py-0.5 rounded ring-1 ${
                                                        STATUS_COLOR[s.status] || "bg-slate-100"
                                                    }`}
                                                    data-testid={`customer-statement-status-${s.id}`}
                                                >
                                                    {STATUS_LABEL[s.status] || s.status}
                                                </span>
                                            </div>
                                            <div className="text-xs text-slate-500">
                                                Period {formatStatementPeriod(
                                                    s.period_start_date || s.period_start,
                                                    s.period_end_date || s.period_end,
                                                )}
                                                {" · "}
                                                {(s.invoice_ids || []).length} invoice(s)
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="font-semibold text-slate-900"
                                                 data-testid={`customer-statement-total-${s.id}`}>
                                                {fmtMoney(s.total_cents)}
                                            </div>
                                        </div>
                                        <Button asChild size="sm" variant="outline">
                                            <Link
                                                to={`/customer/statements/${s.id}`}
                                                data-testid={`customer-statement-open-${s.id}`}
                                            >
                                                View <ChevronRight className="h-4 w-4 ml-1" />
                                            </Link>
                                        </Button>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                )}
            </div>
        </DashboardShell>
    );
}
